import React from 'react';
import {
  Box, Heading, Text, HStack, Stack, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  Spinner, Alert, AlertIcon,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

/**
 * Consistent page wrapper used by all admin pages.
 * Provides title, breadcrumb, loading state, and error display.
 */
const PageShell = ({ title, subtitle, breadcrumbs, actions, loading, error, children }) => {
  const { t } = useTranslation();
  return (
    <Box>
      {/* Breadcrumb */}
      {breadcrumbs && (
        <Breadcrumb separator={<ChevronRightIcon color="gray.400" />} mb={2} fontSize="sm" color="gray.500">
          <BreadcrumbItem>
            <BreadcrumbLink href="/">{t('common.home')}</BreadcrumbLink>
          </BreadcrumbItem>
          {breadcrumbs.map((b, i) => (
            <BreadcrumbItem key={i} isCurrentPage={i === breadcrumbs.length - 1}>
              <BreadcrumbLink href={b.href}>{b.label}</BreadcrumbLink>
            </BreadcrumbItem>
          ))}
        </Breadcrumb>
      )}

      {/* Header row — stacks on phone so action buttons get a full row
          instead of being squeezed beside the title. */}
      <Stack
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        spacing={3}
        mb={{ base: 4, md: 6 }}
      >
        <Box minW={0}>
          <Heading size={{ base: 'md', md: 'lg' }}>{title}</Heading>
          {subtitle && <Text color="gray.500" fontSize="sm" mt={1}>{subtitle}</Text>}
        </Box>
        {actions && <HStack spacing={3} flexWrap="wrap" rowGap={2}>{actions}</HStack>}
      </Stack>

      {/* Error */}
      {error && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box textAlign="center" py={20}>
          <Spinner size="lg" color="blue.500" />
          <Text mt={3} color="gray.500" fontSize="sm">{t('common.loading')}</Text>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

export default PageShell;
